import {
  Content,
  ChannelPane,
  PlayableList,
  Playlist,
  Schedule,
  Screen,
  Playable,
  MediaContent,
  CacheableResource,
  effectiveImageQuality,
  ImageQuality,
  ImageUrls,
} from './types'

/**
 * Pick the delivery URL matching the screen's image quality.
 * The server supplies `urls` per image; anything without it (older content, non-Cloudflare
 * storage, a player build predating the field) falls back to the canonical `url`.
 */
export function resolveImageUrl(
  url: string,
  urls: ImageUrls | undefined,
  quality: ImageQuality = ImageQuality.HD
): string {
  return urls?.[quality] ?? url
}

export function insertInArray<T>(array: T[], index: number, item: T) {
  array.splice(index, 0, item)
  return array
}

// mutates provided array
export function removeInArray<T>(array: T[], index: number): T[] {
  return array.splice(index, 1) // return removed elements in list
}

// mutates provided array
export function moveInArray<T>(array: T[], oldIndex: number, newIndex: number) {
  array.splice(newIndex, 0, removeInArray(array, oldIndex)[0])
  return array
}

const hoursToSeconds = (hours: number) => hours * 60 * 60
export const daysInSeconds = (days: number) => days * hoursToSeconds(24)

const ensure2Digits = (number: number) => (number < 10 ? `0${number}` : number)

export function secondsToTimeFormat(seconds: number) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - hours * 3600) / 60)
  const secondsLeft = seconds - hours * 3600 - minutes * 60

  return [hours, minutes, secondsLeft].map(ensure2Digits).join(':')
}

export function timeFormatToSeconds(time: string) {
  const [hours, minutes, seconds] = time.split(':').map(Number)
  return hours * 3600 + minutes * 60 + seconds
}

export function getContentDuration(content: Content, defaultDuration: number): number {
  if ('duration' in content && content.duration) {
    return content.duration
  }
  return defaultDuration
}

export function extractId(url: string, list: boolean): { videoOrPlaylistId: string; isPlaylist: boolean } {
  let parsedUrl: URL | undefined = undefined
  try {
    parsedUrl = new URL(url)
  } catch (error: any) {
    console.error('Invalid URL:', error)
    return { videoOrPlaylistId: url, isPlaylist: list }
  }

  const params = parsedUrl.searchParams

  const videoId = params.get('v')
  const playlistId = params.get('list')

  if (videoId) {
    return { videoOrPlaylistId: videoId, isPlaylist: false }
  } else if (playlistId) {
    return { videoOrPlaylistId: playlistId, isPlaylist: true }
  } else {
    return { videoOrPlaylistId: url, isPlaylist: list }
  }
}

export function unwrapPlaylist(playlist?: Playlist, schedule?: Schedule): PlayableList {
  if (!playlist) return []
  // it is critical that same schedule reference is used for all items (no cloning!)
  return playlist.items.map(({ duration, content, canvasLayout }) => ({ duration, content, canvasLayout, schedule }))
}

export function unwrapChannelPane(pane?: ChannelPane): PlayableList {
  if (!pane) return []
  return pane.items.flatMap((item) => {
    const { duration, schedule } = item
    if (item.content) {
      const { content } = item
      // it is critical that same schedule reference is used for all items (no cloning!)
      return { duration, content, schedule }
    }

    if (item.canvasLayout) {
      const { canvasLayout } = item
      return { duration, canvasLayout, schedule }
    }

    return unwrapPlaylist(item.playlist, schedule)
  })
}

// CachablePlayable is a playable that has a content with url that can be cached (e.g. video or image)
type CachablePlayable = Omit<Playable, 'content'> & { content: MediaContent }

function isCachablePlayable(playable: Playable): playable is CachablePlayable {
  if (!playable.content) return false // do not support canvas layout yet
  return playable.content.type === 'video' || playable.content.type === 'image'
}

function getCachablePlayables(screen: Screen): CachablePlayable[] {
  let playables: PlayableList = []

  if (screen.playlist) {
    playables = unwrapPlaylist(screen.playlist)
  } else if (screen.channel) {
    screen.channel.panes.forEach((pane) => {
      playables = playables.concat(unwrapChannelPane(pane))
    })
  }

  // we should only return image and video contents since other content types cannot be cached
  return playables.filter(isCachablePlayable)
}

/** The variant the player will actually render, so a warmed cache is the one that gets hit. */
export function toCacheableResource(content: MediaContent, imageQuality?: Screen['imageQuality']): CacheableResource {
  if (content.type === 'video') {
    // TODO: backend shall return direct manifest URL after some weeks
    const manifestUrl = content.url.replace('/iframe', '/manifest/video.mpd')
    return { id: content.id, type: 'video' as const, name: content.name, urls: [manifestUrl] }
  }
  const imageUrl = resolveImageUrl(content.url, content.urls, imageQuality)
  return { id: content.id, type: 'image' as const, name: content.name, urls: [imageUrl] }
}

export function getCachablePlayablesUrls(screen: Screen): CacheableResource[] {
  const playables = getCachablePlayables(screen)
  return playables.map((playable) => {
    const { content } = playable
    if (content.type === 'video') {
      // TODO: backend shall return direct manifest URL after some weeks
      const manifestUrl = content.url.replace('/iframe', '/manifest/video.mpd')
      return { id: content.id, type: 'video' as const, name: content.name, urls: [manifestUrl] }
    }
    // Must match the variant the player renders, otherwise every image misses the cache.
    const imageUrl = resolveImageUrl(content.url, content.urls, effectiveImageQuality(screen))
    return { id: content.id, type: 'image' as const, name: content.name, urls: [imageUrl] }
  })
}

export function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
}

export function getAppUrl(appId: string, configMetadata?: Record<string, any>): string {
  // Trailing slash is needed to avoid 301 redirects (also more secure for resolving relative assets
  // in index.html, i.e. against /apps/appId/ rather than /apps/).
  // Resolving relative assets wrongly was experienced in Android app when okhttp handled redirects automatically
  let relativePath = `apps/${appId}/`
  if (configMetadata) {
    const params = new URLSearchParams()
    params.set('config', JSON.stringify(configMetadata))
    relativePath += `?${params.toString()}`
  }
  return isLocalhost() ? `http://localhost:8765/${relativePath}` : `/${relativePath}`
}

export function getAppIconUrl(appId: string): string {
  return getAppUrl(appId) + 'icon.svg'
}

export function getAppPreviewUrl(appId: string, sampleImageUrl?: string): string {
  if (sampleImageUrl && (sampleImageUrl.startsWith('http://') || sampleImageUrl.startsWith('https://'))) {
    return sampleImageUrl
  }
  return getAppUrl(appId) + (sampleImageUrl || 'preview.jpg')
}
